<div *transloco="let t">
	<ng-container>
		<div mat-dialog-title class="dialog-header" cdkDrag cdkDragHandle cdkDragRootElement=".cdk-overlay-pane">
			<span>{{ t(getTitleKey()) }}</span>
			<button mat-icon-button type="button" (click)="close(false)" class="close-btn" [attr.aria-label]="t('commonActions.close')">
				<mat-icon>close</mat-icon>
			</button>
		</div>
		<div class="flex flex-col">
			<mat-dialog-content>
				<div [formGroup]="costGroupFg" class="flex flex-col">
					@if (currentCostGroupType.category === CostGroupCategory.HEAT_ENERGY) {
						<mat-form-field appearance="outline" class="m-2 w-100">
							<mat-label>{{ t('billing.template') }}</mat-label>
							<mat-select
								(selectionChange)="updateSwitchesAndNames('template')"
								formControlName="template"
								panelClass="!max-h-[32rem] !overflow-y-auto">
								<mat-select-trigger>
									@let template = costGroupFg.controls.template.value;
									<div class="flex flex-row min-w-50 font-bold gap-1">
										<span>{{ t('commodity.' + template?.commodity) }}</span>
										<span>in</span>
										<span>{{ t('enum.' + template?.physicalUnit) }}</span>
									</div>
								</mat-select-trigger>

								@for (template of costGroupTemplates(); track template) {
									<mat-option [value]="template" class="mb-2">
										<div class="flex flex-row min-w-50 font-bold gap-1">
											<span>{{ t('commodity.' + template?.commodity) }}</span>
											<span>in</span>
											<span>{{ t('enum.' + template?.physicalUnit) }}</span>
										</div>
										<div class="inline-flex items-center leading-none text-xs text-gray-500 gap-1">
											<span>
												CO
												<sub>2</sub>
											</span>
											<span>
												@if (template?.hasCo2) {
													{{ t('common.afflicted') }}
												} @else {
													{{ t('common.free') }}
												}
											</span>
											<span>|</span>
											<span>
												@if (template?.invoiceCategoryTypes.includes(InvoiceCategoryType.DELIVERY)) {
													{{ t('enum.stockable') }}
												} @else {
													{{ t('enum.consumption') }}
												}
											</span>
										</div>
									</mat-option>
								}
							</mat-select>
						</mat-form-field>

						<div class="flex items-center justify-start w-full pt-4">
							<div class="text-black pr-4 whitespace-nowrap flex-shrink-0 font-bold">
								{{ t('common.or') }}
							</div>
							<hr class="w-full my-4" />
						</div>

						<div class="flex flex-row items-center justify-start">
							<mat-form-field appearance="outline" class="mx-2 w-[75%]">
								<mat-label>{{ t('billing.commodity') }}</mat-label>
								<mat-select
									(selectionChange)="updateSwitchesAndNames('commodity')"
									formControlName="commodity"
									panelClass="!max-h-[32rem] !overflow-y-auto">
									<mat-select-trigger>
										{{ getCommodityName() }}
									</mat-select-trigger>
									<mat-option [value]="currentCommodity()">
										<div class="font-bold">
											{{ t('commodity.' + currentCommodity()?.commodity) }}
										</div>
									</mat-option>
								</mat-select>
							</mat-form-field>

							<mat-form-field appearance="outline" class="mx-2 w-[25%]">
								<mat-label>{{ t('billing.unit') }}</mat-label>
								<mat-select formControlName="unit" panelClass="!max-h-[32rem] !overflow-y-auto">
									@if (!selectedByTemplate()) {
										@for (physicalUnit of this.costGroupFg.get('commodity')?.value?.validUnits; track physicalUnit) {
											<mat-option [value]="physicalUnit">
												<div class="font-bold">
													{{ t('enum.' + physicalUnit) }}
												</div>
											</mat-option>
										}
									} @else {
										@for (physicalUnit of [currentCommodity()?.physicalUnit]; track physicalUnit) {
											<mat-option [value]="physicalUnit">
												<div class="font-bold">
													{{ t('enum.' + physicalUnit) }}
												</div>
											</mat-option>
										}
									}
								</mat-select>
							</mat-form-field>
						</div>
						<div class="flex flex-col">
							@if (
								!this.selectedPeriodService.selectedPeriod().generalRegulatoryMode ||
								this.selectedPeriodService.selectedPeriod().generalRegulatoryMode === 'GERMAN_STANDARD'
							) {
								<mat-slide-toggle (change)="switchCo2()" [checked]="isShowCo2()" [disabled]="true" class="mx-2 text-[13px]" color="primary">
									@if (this.isShowCo2()) {
										<p [innerHTML]="t('billing.co2BoundShort')" class="mx-2"></p>
									} @else {
										<p [innerHTML]="t('billing.co2FreeShort')" class="mx-2"></p>
									}
								</mat-slide-toggle>
								@if (this.isShowCo2()) {
									<p [innerHTML]="t('billing.co2Bound')" class="mx-2"></p>
								} @else {
									<p [innerHTML]="t('billing.co2Free')" class="mx-2"></p>
								}
							}
							<div>
								<mat-slide-toggle
									(change)="switchInStock()"
									[checked]="isShowInStock()"
									[disabled]="true"
									class="mx-2 mt-4 text-[13px]"
									color="primary">
									@if (isShowInStock()) {
										<div [innerHTML]="t('billing.inStock')"></div>
									} @else {
										<div [innerHTML]="t('billing.consumption')"></div>
									}
								</mat-slide-toggle>
								@if (this.isShowInStock()) {
									<p class="mx-2">
										{{ t('billing.inStockDescription') }}
									</p>
								} @else {
									<p class="mx-2">
										{{ t('billing.inConsumptionDescription') }}
									</p>
								}
							</div>
						</div>
					} @else if (
						currentCostGroupType.category === CostGroupCategory.ANCILLARY_SERVICES ||
						currentCostGroupType.category === CostGroupCategory.HEATING_OPERATION
					) {
						<mat-form-field appearance="outline" class="m-2 w-100">
							<mat-label>{{ t('billing.template') }}</mat-label>
							<mat-select
								(selectionChange)="updateSwitchesAndNames('other')"
								formControlName="template"
								panelClass="!max-h-[32rem] !overflow-y-auto">
								@for (template of costGroupTemplates(); track template) {
									<mat-option [value]="template" class="mb-2">
										<div class="flex flex-row min-w-50">
											<div class="font-bold">
												{{ t('billing.bvedCostTypes.' + template?.costTemplate) }}
											</div>
										</div>
										<div class="grid grid-cols-1 gap-1">
											<div class="font-light">
												{{ t('billing.costType_Short') + ': ' + t('billing.costTypes.' + template?.costType) }}
											</div>
										</div>
									</mat-option>
								}
							</mat-select>
						</mat-form-field>

						<div class="flex items-center justify-start w-full pt-4">
							<div class="text-black pr-4 whitespace-nowrap flex-shrink-0 font-bold">
								{{ t('common.or') }}
							</div>
							<hr class="w-full my-4" />
						</div>

						<mat-checkbox class="py-2" formControlName="hasInvoiceAmount">
							{{ t('billing.invoiceAmountType.INVOICED_AMOUNT') }}
						</mat-checkbox>
						<div class="select-container flex flex-row justify-start items-center w-full gap-2">
							<mat-form-field appearance="outline" class="mx-2 py-2 w-full">
								<mat-label>{{ t('billing.WAGE_35a') }}</mat-label>
								<mat-select
									(selectionChange)="updateSwitchesAndNames('wage')"
									formControlName="selectedWage"
									panelClass="!max-h-[32rem] !overflow-y-auto">
									<mat-select-trigger>
										{{ t('billing.invoiceAmountType.' + costGroupFg.get('selectedWage').value) }}
										@if (costGroupFg.get('selectedWage').value && !costGroupFg.get('selectedWage').disabled) {
											<mat-icon
												class="mat-select-clear-icon"
												[matTooltip]="t('commonActions.deleteSelection')"
												(click)="$event.stopPropagation(); costGroupFg.get('selectedWage').setValue(undefined)"
												(keydown.enter)="$event.stopPropagation(); costGroupFg.get('selectedWage').setValue(undefined)">
												clear
											</mat-icon>
										}
									</mat-select-trigger>
									@for (currentWage of wages; track currentWage) {
										<mat-option [value]="currentWage">
											<div class="font-bold">
												{{ t('billing.invoiceAmountType.' + currentWage) }}
											</div>
										</mat-option>
									}
								</mat-select>
							</mat-form-field>
						</div>
					} @else if (currentCostGroupType.category === CostGroupCategory.TENANT_ELECTRICITY) {
						<!-- TenantElectriciy -->
						<mat-form-field appearance="outline" class="m-2 w-100">
							<mat-label>{{ t('billing.template') }}</mat-label>
							<mat-select
								(selectionChange)="updateSwitchesAndNames('tenant_electricity')"
								formControlName="template"
								panelClass="!max-h-[32rem] !overflow-y-auto">
								@for (template of costGroupTemplates(); track template) {
									<mat-option [value]="template" class="mb-2">
										<div class="flex flex-row min-w-50">
											<div class="font-bold">
												{{ t('billing.bvedCostTypes.' + template?.costTemplate) }}
											</div>
										</div>
										<div class="grid grid-cols-2 gap-1">
											<div class="font-light">
												{{ t('commodity.' + template?.commodity) }}
												in {{ t('enum.' + template?.physicalUnit) }}
											</div>
										</div>
									</mat-option>
								}
							</mat-select>
						</mat-form-field>

						<div class="flex items-center justify-start w-full pt-4">
							<div class="text-black pr-4 whitespace-nowrap flex-shrink-0 font-bold">
								{{ t('common.or') }}
							</div>
							<hr class="w-full my-4" />
						</div>

						<div class="flex flex-row items-center justify-start">
							<mat-form-field appearance="outline" class="mx-2 w-[75%]">
								<mat-label>{{ t('billing.commodity') }}</mat-label>
								<mat-select
									(selectionChange)="updateSwitchesAndNames('commodity')"
									formControlName="commodity"
									panelClass="!max-h-[32rem] !overflow-y-auto">
									<mat-select-trigger>
										{{ getCommodityName() }}
									</mat-select-trigger>
									<mat-option [value]="currentCommodity()">
										<div class="font-bold">
											{{ t('commodity.' + currentCommodity()?.commodity) }}
										</div>
									</mat-option>
								</mat-select>
							</mat-form-field>

							<mat-form-field appearance="outline" class="mx-2 w-[25%]">
								<mat-label>{{ t('billing.unit') }}</mat-label>
								<mat-select formControlName="unit" panelClass="!max-h-[32rem] !overflow-y-auto">
									@if (!selectedByTemplate()) {
										@for (physicalUnit of this.costGroupFg.get('commodity')?.value?.validUnits; track physicalUnit) {
											<mat-option [value]="physicalUnit">
												<div class="font-bold">
													{{ t('enum.' + physicalUnit) }}
												</div>
											</mat-option>
										}
									} @else {
										@for (physicalUnit of [currentCommodity()?.physicalUnit]; track physicalUnit) {
											<mat-option [value]="physicalUnit">
												<div class="font-bold">
													{{ t('enum.' + physicalUnit) }}
												</div>
											</mat-option>
										}
									}
								</mat-select>
							</mat-form-field>
						</div>
						<div class="flex flex-col">
							<div>
								<mat-slide-toggle
									(change)="switchFeedIn()"
									[checked]="isFeedIn()"
									[disabled]="true"
									class="mx-2 mt-0 text-[13px]"
									color="primary">
									@if (isFeedIn()) {
										<div [innerHTML]="t('billing.feedIn')"></div>
									} @else {
										<div [innerHTML]="t('billing.directConsumption')"></div>
									}
								</mat-slide-toggle>
								@if (isFeedIn()) {
									<p class="mx-2">
										{{ t('billing.feedInDescription') }}
									</p>
								} @else {
									<p class="mx-2">
										{{ t('billing.inConsumptionDescription') }}
									</p>
								}
							</div>
						</div>

						<div class="flex items-center justify-start w-full pt-4">
							<div class="text-black pr-4 whitespace-nowrap flex-shrink-0 font-bold">
								{{ t('billing.invoiceAndEnergymix') }}
							</div>
							<hr class="w-full my-4" />
						</div>

						<div class="flex flex-col mb-2">
							<mat-slide-toggle
								(change)="switchInvoiceAmountType()"
								[checked]="isInvoicedAmount()"
								[disabled]="true"
								class="mx-2 text-[13px]"
								color="primary">
								@if (isInvoicedAmount()) {
									<p class="mx-2">
										{{ t('billing.invoiceAmountType.INVOICED_AMOUNT') }}
									</p>
								} @else {
									<p class="mx-2">
										{{ t('billing.invoiceAmountType.CREDIT_AMOUNT') }}
									</p>
								}
							</mat-slide-toggle>
							@if (isInvoicedAmount()) {
								<p class="mx-2 text-sm text-gray-600">
									{{ t('billing.invoiceAmountDescription') }}
								</p>
							} @else {
								<p class="mx-2 text-sm text-gray-600">
									{{ t('billing.creditAmountDescription') }}
								</p>
							}
						</div>

						<div class="grid grid-cols-2 gap-x-1 gap-y-0">
							<mat-checkbox (change)="switchCo2Emission()" [checked]="isShowCo2Emission()" [disabled]="true" class="py-0">
								{{ t('commodity.CO2_EMISSION') + ' in ' + t('enum.G_TO_KWH') }}
							</mat-checkbox>
							<mat-checkbox (change)="switchRadioactiveWaste()" [checked]="isShowRadioactiveWaste()" [disabled]="true" class="py-0">
								{{ t('commodity.RADIOACTIVE_WASTE') + ' in ' + t('enum.G_TO_KWH') }}
							</mat-checkbox>
							<mat-checkbox (change)="switchRenewableEnergyShareEEG()" [checked]="isShowRenewableEnergyShareEEG()" [disabled]="true" class="py-0">
								{{ t('commodity.RENEWABLE_ENERGY_SHARE_EEG') + ' in ' + t('enum.PERCENT') }}
							</mat-checkbox>
							<mat-checkbox (change)="switchRenewableEnergyShare()" [checked]="isShowRenewableEnergyShare()" [disabled]="true" class="py-0">
								{{ t('commodity.RENEWABLE_ENERGY_SHARE') + ' in ' + t('enum.PERCENT') }}
							</mat-checkbox>
							<mat-checkbox (change)="switchNuclearEnergyShare()" [checked]="isShowNuclearEnergyShare()" [disabled]="true" class="py-0">
								{{ t('commodity.NUCLEAR_ENERGY_SHARE') + ' in ' + t('enum.PERCENT') }}
							</mat-checkbox>
							<mat-checkbox (change)="switchCoalEnergyShare()" [checked]="isShowCoalEnergyShare()" [disabled]="true" class="py-0">
								{{ t('commodity.COAL_ENERGY_SHARE') + ' in ' + t('enum.PERCENT') }}
							</mat-checkbox>
							<mat-checkbox (change)="switchGasEnergyShare()" [checked]="isShowGasEnergyShare()" [disabled]="true" class="py-0">
								{{ t('commodity.GAS_ENERGY_SHARE') + ' in ' + t('enum.PERCENT') }}
							</mat-checkbox>
							<mat-checkbox (change)="switchOtherFossileEnergyShare()" [checked]="isShowOtherFossileEnergyShare()" [disabled]="true" class="py-0">
								{{ t('commodity.OTHER_FOSSILE_ENERGY_SHARE') + ' in ' + t('enum.PERCENT') }}
							</mat-checkbox>
						</div>
					}

					<div class="flex items-center justify-start w-full pt-4">
						<div class="text-black pr-4 whitespace-nowrap flex-shrink-0 font-bold">
							{{ t('billing.costType') }}
						</div>
						<hr class="flex-grow my-4" />
						<div class="flex items-center ml-4 flex-shrink-0">
							<mat-slide-toggle [(ngModel)]="bvedSectionExpanded" [ngModelOptions]="{ standalone: true }" color="primary" class="text-sm">
								{{ t('billing.customBvedType') }}
							</mat-slide-toggle>
							<mat-icon class="text-gray-500 !text-base !w-4 !h-4 !leading-4 ml-1" [matTooltip]="t('billing.bvedCostTypeTooltip')">
								info_outline
							</mat-icon>
						</div>
					</div>

					<div class="flex flex-row">
						<mat-form-field appearance="outline" class="flex-grow mx-2">
							<mat-label>{{ t('billing.costTypeSelect') }}</mat-label>
							<mat-select class="w-full" formControlName="selectedCostType" panelClass="!max-h-[32rem] !overflow-y-auto">
								<mat-select-trigger>
									{{ t('billing.costTypes.' + costGroupFg.get('selectedCostType').value) }}
									@if (costGroupFg.get('selectedCostType').value && !costGroupFg.get('selectedCostType').disabled) {
										<mat-icon
											class="mat-select-clear-icon"
											[matTooltip]="t('commonActions.deleteSelection')"
											(click)="$event.stopPropagation(); costGroupFg.get('selectedCostType').setValue(undefined)">
											clear
										</mat-icon>
									}
								</mat-select-trigger>
								@for (costType of costTypeMeta; track costType) {
									<mat-option [value]="costType.costType">
										{{ t('billing.costTypes.' + costType.costType) }}
									</mat-option>
								}
							</mat-select>
						</mat-form-field>
						<mat-form-field appearance="outline" class="w-36 mx-2">
							<mat-label>{{ t('billing.preferredVatRateShort') }}</mat-label>
							<input
								formControlName="preferredVatRate"
								matInput
								araDecimalFormat
								[allowNegative]="false"
								[decimalFractionDigitsMin]="0"
								[decimalFractionDigitsMax]="0"
								class="text-right" />
							@if (costGroupFg.get('preferredVatRate')?.value != null && costGroupFg.get('preferredVatRate')?.value !== '') {
								<span matSuffix class="text-lg font-extrabold">%&nbsp;</span>
							}
						</mat-form-field>
					</div>
					@if (bvedSectionExpanded) {
						<mat-form-field appearance="outline" class="w-full mx-2">
							<mat-label>{{ t('billing.bvedCostTypeSelect') }}</mat-label>
							<mat-select class="w-full" formControlName="selectedBvedCostType" panelClass="!max-h-[32rem] !overflow-y-auto">
								<mat-select-trigger>
									{{ t('billing.bvedCostTypes.' + costGroupFg.get('selectedBvedCostType').value) }}
									@if (costGroupFg.get('selectedBvedCostType').value && !costGroupFg.get('selectedBvedCostType').disabled) {
										<mat-icon
											class="mat-select-clear-icon"
											[matTooltip]="t('commonActions.deleteSelection')"
											(click)="$event.stopPropagation(); costGroupFg.get('selectedBvedCostType').setValue(undefined)"
											(keydown.enter)="$event.stopPropagation(); costGroupFg.get('selectedBvedCostType').setValue(undefined)">
											clear
										</mat-icon>
									}
								</mat-select-trigger>
								<mat-option [value]="currentCommodity()?.bvedCostType">
									<!-- Erste Zeile: Übersetzung -->
									<div class="flex flex-col py-1">
										<div class="font-bold">
											{{ t('billing.bvedCostTypes.' + currentCommodity()?.bvedCostType) }}
										</div>
										<!-- Zweite Zeile: Key und Kategorie -->
										<div class="font-light">
											Key: {{ getBvedCostTypeKey(currentCommodity()?.bvedCostType) }}, {{ t('billing.category') }}:
											{{ t(getBvedMainCategoryI18nKey(getBvedMainCategory(currentCommodity()?.bvedCostType))) }}
										</div>
									</div>
								</mat-option>
							</mat-select>
						</mat-form-field>
					}

					<div class="flex items-center justify-start w-full pt-4">
						<div class="text-black pr-4 whitespace-nowrap flex-shrink-0 font-bold">
							{{ t('billing.allocation') }}
						</div>
						<hr class="w-full my-4" />
					</div>

					@if (allocations().length > 0) {
						<div class="flex items-center w-full">
							<mat-form-field appearance="outline" class="flex-grow mx-2">
								<mat-label>{{ t('billing.allocationSelect') }}</mat-label>
								<mat-select class="w-full" formControlName="selectedAllocation" panelClass="!max-h-[32rem] !overflow-y-auto">
									<mat-select-trigger>
										{{ getAllocationName() }}
										@if (costGroupFg.get('selectedAllocation').value && !costGroupFg.get('selectedAllocation').disabled) {
											<mat-icon
												class="mat-select-clear-icon"
												[matTooltip]="t('commonActions.deleteSelection')"
												(click)="$event.stopPropagation(); costGroupFg.get('selectedAllocation').setValue(undefined)"
												(keydown.enter)="$event.stopPropagation(); costGroupFg.get('selectedAllocation').setValue(undefined)">
												clear
											</mat-icon>
										}
									</mat-select-trigger>
									@for (currentAllocation of allocations(); track currentAllocation.exportId) {
										<mat-option [value]="currentAllocation">
											<vex-allocation-option [allocation]="currentAllocation" />
										</mat-option>
									}
								</mat-select>
							</mat-form-field>
						</div>
					} @else {
						<div class="flex items-center gap-3 mx-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
							<mat-icon class="text-gray-500">info_outline</mat-icon>
							<span class="text-sm text-gray-600 flex-grow">{{ t('billing.noAllocationAvailable') }}</span>
						</div>
					}

					<div class="flex items-center justify-start w-full pt-4">
						<div class="text-black pr-4 whitespace-nowrap flex-shrink-0 font-bold">
							{{ t('common.label') }}
						</div>
						<hr class="w-full my-4" />
					</div>

					<div class="flex flex-col">
						<div class="flex flex-row">
							<mat-form-field appearance="outline" class="mx-2 w-[50%]">
								<mat-label>{{ t('billing.name') }}</mat-label>
								<input [matTooltip]="costGroupFg.get('name')?.value ?? ''" formControlName="name" matInput />
							</mat-form-field>
							<mat-form-field appearance="outline" class="mx-2 w-[50%]">
								<mat-label>{{ t('common.printName') }}</mat-label>
								<input [matTooltip]="costGroupFg.get('printName')?.value ?? ''" formControlName="printName" matInput />
							</mat-form-field>
						</div>
						<div class="flex flex-row">
							<mat-form-field appearance="outline" class="mx-2 w-full">
								<mat-label>{{ t('billing.description') }}</mat-label>
								<input formControlName="description" matInput />
							</mat-form-field>
						</div>
					</div>
				</div>
			</mat-dialog-content>
		</div>
	</ng-container>
	<ng-container>
		<mat-dialog-actions class="flex flex-row justify-end items-center" mat-dialog-actions>
			<div>
				@if (!selectedPeriodService.selectedPeriod().locked) {
					<button (click)="close(true)" [disabled]="!costGroupFg.valid || (isEditMode() && !hasChanges())" color="primary" mat-raised-button>
						<mat-icon>check</mat-icon>
						@if (!layoutService.isMobile()) {
							<span>
								{{ t('common.apply') }}
							</span>
						}
					</button>
				}
			</div>
		</mat-dialog-actions>
	</ng-container>
</div>
